/* eslint react-hooks/set-state-in-effect: ["warn"] */
import { useState, useContext, useMemo, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router";
import styles from "./activity-overview.module.css";
import { TaskStateTabs } from "./states/TaskStateTabs";
import { ActivityColumn } from "./column/ActivityColumn";
import { ActivityFilterType } from "../types/filterOptionsTypes";
import { DEFAULT_FILTER_VALUES } from "../constants/defaultFilterValues";
import { useActivityData } from "../hooks/useActivityData";
import { useTranslation } from "@coworker/locales";
import { ActivityColumns } from "../constants/activityColumns";
import { FilterPanel } from "../../shared/filters/components/FilterPanel";
import { useTasksFilters } from "../hooks/useTasksFilters";
import { mapFilterValuesToObject } from "../helpers/mapFilterValuesToObject";
import FixaButton from "../../shared/wrappers/FixaButton/FixaButton";
import StackIconPath from "../../shared/wrappers/FixaSSRIcon/paths/StackIconPath";
import { MultiSelectPanel } from "./filters/panels/multi-select/MultiSelectPanel";
import { ModalContext } from "../../layout/context/ModalContextProvider";
import { ModalType } from "../../layout/context/modalType";
import { useUserId } from "@coworker/app/src/core/auth/useLoggedInUser";
import trackerHelper from "@coworker/app/src/helpers/tracker";
import { getAppliedFiltersCount } from "@coworker/apprestructured/src/activity/helpers/getAppliedFiltersCount";
import { isEqual } from "@coworker/app/src/helpers/utils";
import { TaskType } from "../../tasks/enums/taskTypes";
import { useTask } from "../hooks/useTask";
import { useQueryClient } from "@tanstack/react-query";

export const TasksOverview = () => {
  const [activeTab, setActiveTab] = useState(ActivityColumns.OPEN);
  const activityFilters = useTasksFilters();
  const [filters, setFilters] = useState<ActivityFilterType>(
    DEFAULT_FILTER_VALUES
  );
  const { toggleModal } = useContext(ModalContext);
  const userId = useUserId();
  const [searchParams, setSearchParams] = useSearchParams();
  const taskDetailsUrlParameter = searchParams.get("taskDetails");
  const { data: taskDetails } = useTask(taskDetailsUrlParameter);
  const queryClient = useQueryClient();

  const newFilters = useMemo(() => {
    // Update filters when activityFilters.filterValues change
    return {
      ...DEFAULT_FILTER_VALUES,
      ...mapFilterValuesToObject(activityFilters.filterValues),
    };
  }, [activityFilters.filterValues]);

  if (!isEqual(filters, newFilters)) setFilters(newFilters);

  useEffect(() => {
    if (filters.assignedTo && userId) {
      if (filters.assignedTo === userId) {
        setActiveTab(ActivityColumns.IN_PROGRESS);
      } else if (filters.assignedTo !== userId) {
        setActiveTab(ActivityColumns.OPEN);
      }
    }
  }, [filters.assignedTo, userId]);

  const { openTasks } = useActivityData(filters, ActivityColumns.OPEN) || [];
  const { inProgressTasks } =
    useActivityData(filters, ActivityColumns.IN_PROGRESS) || [];
  const { completedTasks } =
    useActivityData(filters, ActivityColumns.COMPLETED) || [];
  const { closedTasks } =
    useActivityData(filters, ActivityColumns.CLOSED) || [];
  const { t } = useTranslation();

  const appliedFiltersCount = getAppliedFiltersCount(filters);

  const columns = [
    {
      transTitleKey: "teamOpenString",
      tasks: openTasks,
      tab: ActivityColumns.OPEN,
    },
    {
      transTitleKey: "inProgressString",
      tasks: inProgressTasks,
      tab: ActivityColumns.IN_PROGRESS,
    },
    {
      transTitleKey: "completd3String",
      tasks: completedTasks,
      tab: ActivityColumns.COMPLETED,
    },
    {
      transTitleKey: "closedFirstUppercaseString",
      tasks: closedTasks,
      tab: ActivityColumns.CLOSED,
    },
  ];

  const onShowTaskDetails = useCallback(
    (taskId: string, taskType: TaskType) => {
      toggleModal({
        type: ModalType.TaskDetail,
        payload: { taskId, taskType },
      });
    },
    [toggleModal]
  );

  useEffect(() => {
    if (taskDetailsUrlParameter && taskDetails) {
      const taskType = taskDetails.task_type;
      queryClient.invalidateQueries({
        queryKey: ["activityTasks", "inProgressTasks"],
      });
      queryClient.invalidateQueries({
        queryKey: ["activityTasks", "openTasks"],
      });
      onShowTaskDetails(taskDetailsUrlParameter, taskType);
      searchParams.delete("taskDetails");
      setSearchParams(searchParams, { replace: true });
    }
  }, [
    taskDetailsUrlParameter,
    taskDetails,
    onShowTaskDetails,
    searchParams,
    setSearchParams,
    queryClient,
  ]);

  return (
    <div className={styles["overview-container"]}>
      <div className={styles["title"]}>{t("TasksString")}</div>
      {(activeTab === ActivityColumns.OPEN ||
        activeTab === ActivityColumns.IN_PROGRESS) && (
        <FixaButton
          iconOnly
          ssrIcon={StackIconPath}
          size="small"
          type="emphasised"
          className={styles["multi-select-button"] as string}
          onClick={() => {
            trackerHelper.trackMultiSelectButtonTab(activeTab);
            toggleModal({ type: ModalType.MultiSelectTasks });
          }}
        />
      )}
      <FilterPanel
        filterValues={activityFilters.filterValues}
        setFilterValues={activityFilters.setFilterValues}
        useFiltersFactory={activityFilters.useFiltersFactory}
        feature="tasks"
      />
      <TaskStateTabs
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tasksCount={{
          open: openTasks.data?.pages[0]?.filteredCount || 0,
          inProgress: inProgressTasks.data?.pages[0]?.filteredCount || 0,
          completed: completedTasks.data?.pages[0]?.filteredCount || 0,
          closed: closedTasks.data?.pages[0]?.filteredCount || 0,
        }}
        openTasksLoading={openTasks.isLoading}
        inProgressTasksLoading={inProgressTasks.isLoading}
        completedTasksLoading={completedTasks.isLoading}
        closedTasksLoading={closedTasks.isLoading}
      />
      <div className={styles["overview-wrapper"]}>
        {columns.map((column) => {
          return (
            <ActivityColumn
              key={column.tab}
              activeTab={activeTab}
              title={column.transTitleKey}
              tasks={
                column.tasks?.data?.pages.map((page) => page.list).flat() || []
              }
              filteredCount={column.tasks?.data?.pages[0]?.filteredCount || 0}
              totalCount={column.tasks?.data?.pages[0]?.totalCount || 0}
              loadNextPage={column.tasks?.fetchNextPage}
              tab={column.tab}
              appliedFiltersCount={appliedFiltersCount}
              resetFilters={() => {
                activityFilters.resetFilters();
              }}
              isLoading={column.tasks.isLoading}
              isFetching={column.tasks.isFetching}
              groupBy={filters.groupBy ?? ""}
              onShowTaskDetails={onShowTaskDetails}
            />
          );
        })}
      </div>
      <MultiSelectPanel
        filters={filters}
        onShowTaskDetails={onShowTaskDetails}
      />
    </div>
  );
};
